import { inject } from '@angular/core';
import type { CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { Router } from '@angular/router';
import { map, catchError, of } from 'rxjs';
import { AuthService } from '../core/services/auth.service';
import { CycleManagementService } from '../core/services/cycle-management.service';
import { ManagerDashboardService } from '../core/services/manager-dashboard.service';
import type { IUser, SystemRole } from '../shared/models/user.model';

/**
 * Helper to get currently selected role object from user data
 */
function getCurrentRoleObject(userData: IUser | null, authService: AuthService): SystemRole | null {
  if (!userData?.system_role_info?.length) {
    return null;
  }

  // Get the selected role context from the auth service
  const selectedRoleContext = authService.getSelectedRoleContext();

  // If a role context is set, use it; otherwise fall back to first role
  return selectedRoleContext ?? userData.system_role_info[0];
}

/**
 * Helper to check user access and redirect if denied
 */
function checkAccessOrRedirect(hasAccess: boolean, router: Router, userData: IUser | null): boolean {
  if (!userData || !hasAccess) {
    router.navigate(['/access-denied']);
    return false;
  }

  return true;
}/**
 * Permission guard that checks if user has required privileges
 * Based on the privilege system used in market-admin-dashboard
 */
export const permissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const requiredPrivileges = route.data['requiredPrivileges'] as string[] | undefined;

  // If no privileges required, allow access
  if (!requiredPrivileges?.length) {
    return true;
  }

  const userData = authService.userDataValue;
  const currentRole = getCurrentRoleObject(userData, authService);

  if (!currentRole?.privileges) {
    return checkAccessOrRedirect(false, router, userData);
  }

  // Extract and check privileges
  const userPrivileges = new Set(currentRole.privileges.map((p: { privilege_name: string }) => p.privilege_name));
  const hasPermission = requiredPrivileges.some(privilege => userPrivileges.has(privilege));

  return checkAccessOrRedirect(hasPermission, router, userData);
}

/**
 * Role-based guard for checking if user has specific role
 */
export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const requiredRoles = route.data['requiredRoles'] as string[] | undefined;

  if (!requiredRoles?.length) {
    return true;
  }

  const userData = authService.userDataValue;
  const currentRole = getCurrentRoleObject(userData, authService);
  const hasRole = !!currentRole && requiredRoles.includes(currentRole.role_name);

  return checkAccessOrRedirect(hasRole, router, userData);
};

/**
 * Market guard that checks if user has market_id in currently selected role
 * Used for routes that require market-specific access
 * SYSADMIN role has access to all markets
 */
export const marketGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const userData = authService.userDataValue;
  const currentRole = getCurrentRoleObject(userData, authService);

  // SYSADMIN can access all markets
  const isSysAdmin = currentRole?.role_code === 'SYSADMIN';
  const hasMarketId = !!currentRole?.market_id;

  return checkAccessOrRedirect(isSysAdmin || hasMarketId, router, userData);
};

/**
 * Manager cycle form guard that validates access to specific cycle-manager combinations
 * Access rules:
 * - Manager: can only access their own forms (managerId === user.id)
 * - Market Admin: can access forms in their market
 * - BU Admin: can access forms in their market AND business unit
 * - SYSADMIN: can access all forms
 */
export const managerCycleFormGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const cycleService = inject(CycleManagementService);
  const router = inject(Router);

  const cycleId = Number(route.paramMap.get('cycleId'));
  const managerId = Number(route.paramMap.get('managerId'));
  const userData = authService.userDataValue;
  const currentRole = getCurrentRoleObject(userData, authService);

  if (!userData || !currentRole || !cycleId || !managerId) {
    router.navigate(['/access-denied']);
    return false;
  }

  // Normalize role name and code for comparison
  const roleCode = currentRole.role_code?.toUpperCase().trim();
  // SYSADMIN has access to all
  if (roleCode === 'SYSADMIN') {
    return true;
  }

  // Manager can only access their own forms
  if (userData.id === managerId) {
    return true;
  }

  // For Market Admin and BU Admin, need to check cycle details
  return cycleService.getCycleDetail(cycleId).pipe(
    map(cycle => {
      // Market Admin: check if cycle belongs to their market
      if (roleCode === 'MKTADMIN' || roleCode === 'COMPBP') {
        if (cycle.market_id === currentRole.market_id) {
          return true;
        }
      }

      // BU Admin: check if cycle belongs to their market AND business unit
      if (roleCode === 'BUADMIN') {
        const isBUAdmin = cycle?.business_units?.some(item1 => currentRole?.business_units?.some(item2 => item1.id === item2?.business_unit_id));
        if (cycle.market_id === currentRole.market_id && isBUAdmin) {
          return true;
        }
      }
      router.navigate(['/access-denied']);
      return false;
    }),
    catchError((error) => {
      console.error('Error fetching cycle details:', error);
      router.navigate(['/access-denied']);
      return of(false);
    })
  );
};

/**
 * Sub-manager routes guard that validates access to view specific manager's data
 * Access rules:
 * - SYSADMIN, Market Admin, BU Admin: Full access (no hierarchy check)
 * - Regular Managers: Can only view their direct subordinates (prid must be in their sub-managers list)
 */
export const subManagerGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const managerDashboardService = inject(ManagerDashboardService);
  const router = inject(Router);

  // Check for both 'prid' (sub-manager-cycles) and 'managerPrid' (sub-manager-cycle-forms)
  const managerPrid = route.queryParamMap.get('prid') || route.queryParamMap.get('managerPrid') || '';
  const userData = authService.userDataValue;
  const currentRole = getCurrentRoleObject(userData, authService);

  if (!userData || !currentRole || !managerPrid) {
    router.navigate(['/access-denied']);
    return false;
  }

  // Check if user has MANAGER_DASHBOARD privilege
  const userPrivileges = new Set(
    currentRole?.privileges?.map((p: { privilege_name: string }) => p.privilege_name) || []
  );
  if (!userPrivileges.has('MANAGER_DASHBOARD')) {
    router.navigate(['/access-denied']);
    return false;
  }

  const roleCode = currentRole.role_code?.toUpperCase().trim();

  // Admin roles (SYSADMIN, MKTADMIN, COMPBP, BUADMIN) have full access - no hierarchy check
  if (roleCode === 'SYSADMIN' || roleCode === 'MKTADMIN' || roleCode === 'COMPBP' || roleCode === 'BUADMIN') {
    return true;
  }

  // Check if viewing own data (always allowed)
  if (userData.prid === managerPrid) {
    return true;
  }

  // Get the selected role context for marketId
  const selectedRoleContext = authService.getSelectedRoleContext();
  const marketId = selectedRoleContext?.market_id || currentRole.market_id;

  if (!marketId) {
    router.navigate(['/access-denied']);
    return false;
  }

  // For regular managers, verify the target prid is in their sub-managers list
  return managerDashboardService.getSubManagers(userData.prid, marketId).pipe(
    map(subManagers => {
      // Check if the target prid is in the user's sub-managers list (case-insensitive)
      // Only allow access to registered system managers
      const hasAccess = subManagers.some(
        subManager => subManager.prid.toLowerCase() === managerPrid.toLowerCase() && subManager.is_system_manager
      );

      if (!hasAccess) {
        router.navigate(['/access-denied']);
        return false;
      }

      return true;
    }),
    catchError((error) => {
      console.error('Error validating sub-manager access:', error);
      router.navigate(['/access-denied']);
      return of(false);
    })
  );
};
